import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, tap, map, from } from 'rxjs';
import { FolhaServico } from 'src/app/models/folha-servico';
import { StorageService } from '../storage-service/storage.service';
import { FolhaServicoResumo } from './FolhaServicoResumo';
import { PesquisaTransferenciaResponseDTO, TransferenciaTarefaRequestDTO, TransferenciaResultadoDTO, FolhaTransferenciaDTO, CriarOuObterFolhaRequestDTO, CriarOuObterFolhaResponseDTO } from 'src/app/models/transferencia.models';
import { ElginPrintService } from '../print-service/elgin-print-service';
import { TransferenciaVeiculoPorMotoristaRequestDTO } from '../../models/transferencia.models';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../auth.service';

export interface FolhasPendentesAssinaturaDTO {
  possuiPendencia: boolean;
  mensagem: string;
  datasPapeletas: string[];
  quantidadePapeletas: number;
}

export interface RemoverVeiculoRequestDTO {
  dataServico: string;
  garagemId: number;
  matriculaMotorista: number;
  folhaId: number;
}

@Injectable({
  providedIn: 'root'
})
export class FolhaServicoService {

  private storageService = inject(StorageService);
  private authService = inject(AuthService);

  constructor(private http: HttpClient, private elginPrintService: ElginPrintService) { }

  listarFolhaServicoPorData(dataServico: string): Observable<FolhaServico[]> {
    const params = new HttpParams().set('data', dataServico);

    return this.http.get<FolhaServico[]>(`/folha-servico/por-data`, { params });
  }

  buscarFolhaServico(matricula: number, dataServico: string, folhaServicoId?: number): Observable<FolhaServico> {
    let params = new HttpParams()
      .set('matricula', matricula.toString())
      .set('dataServico', dataServico);

    if (folhaServicoId) {
      params = params.set('folhaServicoId', folhaServicoId.toString());
    }

    return this.http.get<FolhaServico>(
      `/folha-servico/obter-por-matricula-e-data`,
      { params }
    ).pipe(
      tap(async (folhaServico) => {
        await this.storageService.saveFolhaServicoComTarefas(
          folhaServico,
          folhaServico.tarefas
        );
      })
    );
  }

  finalizarFolhaDeServico(
    id: number,
    horaFim: string,
    latitude: number,
    longitude: number
  ): Observable<any> {

    const body = {
      id,
      horaFim,
      latitude_papeleta_fim: latitude,
      longitude_papeleta_fim: longitude
    };

    return this.http.post(`/folha-servico/finalizar`, body);
  }

  iniciarFolhaDeServico(
    id: number,
    horaInicial: string,
    latitude: number,
    longitude: number
  ): Observable<any> {

    const body = {
      id,
      horaInicial,
      latitude_papeleta_inicio: latitude,
      longitude_papeleta_inicio: longitude
    };

    return this.http.post(`/folha-servico/iniciar`, body);
  }

  buscarFolhaAtivaPorMatricula(matricula: number): Observable<FolhaServico> {

    return this.http.get<FolhaServico>(
      `/folha-servico/ativa-por-matricula/${matricula}`
    ).pipe(
      tap(async (folhaServico) => {
        if (folhaServico?.tarefas) {
          await this.storageService.saveFolhaServicoComTarefas(
            folhaServico,
            folhaServico.tarefas
          );
        }
      })
    );
  }

  vincularMotivosIntervencao(
    folhaId: number,
    motivosIds: number[]
  ): Observable<any> {

    return this.http.post(
      `/folha-servico/${folhaId}/motivos-intervencao`,
      motivosIds
    );
  }

  buscarPorPeriodo(motoristaId: number, dataInicio: string, dataFim: string): Observable<FolhaServicoResumo[]> {
    return this.http.get<FolhaServicoResumo[]>(
      `/folha-servico/motorista/${motoristaId}/periodo?dataInicio=${dataInicio}&dataFim=${dataFim}`
    );
  }

  // Substitua o método pesquisarFolhasTransferencia no service

  pesquisarFolhasTransferencia(
    veiculoOrigem: string,
    veiculoDestino: string,
    motoristasOrigem: { matricula: string; turno: number }[],
    motoristasDestino: { matricula: string; turno: number }[],
    dataServico?: string
  ): Observable<PesquisaTransferenciaResponseDTO> {
    let params = new HttpParams()
      .set('veiculoOrigem', veiculoOrigem || '')
      .set('veiculoDestino', veiculoDestino || '');

    // Adiciona motoristas origem com seus turnos
    motoristasOrigem.forEach(motorista => {
      if (motorista.matricula && motorista.matricula.trim()) {
        params = params.append('matriculasOrigem', motorista.matricula);
        params = params.append('turnosOrigem', motorista.turno.toString());
      }
    });

    // Adiciona motoristas destino com seus turnos
    motoristasDestino.forEach(motorista => {
      if (motorista.matricula && motorista.matricula.trim()) {
        params = params.append('matriculasDestino', motorista.matricula);
        params = params.append('turnosDestino', motorista.turno.toString());
      }
    });

    // Adiciona data se fornecida
    if (dataServico) {
      params = params.set('dataServico', dataServico);
    }

    return this.http.get<PesquisaTransferenciaResponseDTO>(
      `/folha-servico/pesquisar-transferencia`,
      { params }
    );
  }

  // Transfere tarefas entre folhas de serviço
  transferirTarefas(request: TransferenciaTarefaRequestDTO): Observable<TransferenciaResultadoDTO> {
    return this.http.post<TransferenciaResultadoDTO>(
      `/folha-servico/transferir-tarefas`,
      request
    );
  }

  // Busca folha de serviço por veículo e data
  buscarFolhaPorVeiculo(numeroVeiculo: string, dataServico: string): Observable<FolhaServico> {
    const params = new HttpParams()
      .set('numeroVeiculo', numeroVeiculo)
      .set('dataServico', dataServico);

    return this.http.get<FolhaServico>(`/folha-servico/buscar-por-veiculo`, { params });
  }

  // Busca folha de serviço por motorista e data
  buscarFolhaPorMotorista(matricula: number, dataServico: string): Observable<FolhaServico> {
    const params = new HttpParams()
      .set('matricula', matricula.toString())
      .set('dataServico', dataServico);

    return this.http.get<FolhaServico>(`/folha-servico/buscar-por-motorista`, { params });
  }

  imprimirPorMatricula(matricula: number): void {
    const headers = new HttpHeaders({
      'Accept': 'text/plain; charset=iso-8859-1'
    });

    this.http.get(
      `/folha-servico/imprimir-por-matricula/${matricula}`,
      {
        headers: headers,
        responseType: 'text'
      }).subscribe({
        next: (text) => this.elginPrintService.enviarParaImpressora(text)
      });

  }

  transferirVeiculoPorMotorista(request: TransferenciaVeiculoPorMotoristaRequestDTO): Observable<TransferenciaResultadoDTO> {
    return this.http.post<TransferenciaResultadoDTO>(
      `/folha-servico/transferir-veiculo-por-motorista`,
      request
    );
  }

  obterFolhaServicoPorDataEMatricula(dataServico: string, matricula: number): Observable<FolhaTransferenciaDTO> {
    const params = new HttpParams()
      .set('dataServico', dataServico)
      .set('matricula', matricula.toString());

    return this.http.get<FolhaTransferenciaDTO>(
      `/folha-servico/obter-por-matricula-e-data`,
      { params }
    );
  }

  listarFolhaServicoPorDataEMatricula(dataServico: string, matricula: number): Observable<FolhaTransferenciaDTO[]> {
    const params = new HttpParams()
      .set('dataServico', dataServico)
      .set('matricula', matricula.toString());

    return this.http.get<FolhaTransferenciaDTO[]>(
      `/folha-servico/listar-por-matricula-e-data`,
      { params }
    );
  }

  criarOuObterFolha(request: CriarOuObterFolhaRequestDTO): Observable<CriarOuObterFolhaResponseDTO> {
    return this.http.post<CriarOuObterFolhaResponseDTO>(
      `/folha-servico/criar-ou-obter`,
      request
    );
  }

  verificarFolhasPendentesAssinatura(matricula: number): Observable<FolhasPendentesAssinaturaDTO> {
    return this.http.get<FolhasPendentesAssinaturaDTO>(
      `/folha-servico/assinatura/pendentes/matricula/${matricula}`
    );
  }

  inverterMotorista(dataServico: string, motoristaOrigem: string, motoristaDestino: string, motivoEdicao: string) {
    const body = {
      dataServico: dataServico,
      motoristaOrigem: motoristaOrigem,
      motoristaDestino: motoristaDestino,
      motivoEdicao: motivoEdicao
    };

    return this.http.post(`/folha-servico/inverter-motorista`, body);
  }

  inverterVeiculo(dataServico: string, veiculoOrigem: string, veiculoDestino: string, motivoEdicao: string) {
    const body = {
      dataServico: dataServico,
      veiculoOrigem: veiculoOrigem,
      veiculoDestino: veiculoDestino,
      motivoEdicao: motivoEdicao
    };

    return this.http.post(`/folha-servico/inverter-veiculo`, body);
  }

  excluirLogicamenteFolhaServico(id: number): Observable<any> {
    return this.http.delete(`/folha-servico/${id}/excluir-logico`);
  }

  listarFolhasSemMotoristaPorData(dataServico: string, garagemId?: number): Observable<FolhaTransferenciaDTO[]> {
    let params = new HttpParams()
      .set('dataServico', dataServico);

    if (garagemId) {
      params = params.set('garagemId', garagemId.toString());
    }

    return this.http.get<any>(
      `/folha-servico/sem-motorista`,
      { params }
    ).pipe(
      map(response => {
        if (response && response.folhas) {
          return response.folhas;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      })
    );
  }

  /*atribuirMotoristaVeiculo(folhasIds: number[], matriculaMotorista?: number, numeroVeiculo?: number): Observable<any> {
    const body = {
      folhasIds: folhasIds,
      matriculaMotorista: matriculaMotorista || null,
      numeroVeiculo: numeroVeiculo || null
    };

    return this.http.post(
      `/folha-servico/atribuir-motorista-veiculo`,
      body
    );
  }*/

  atribuirMotoristaVeiculo(
    folhasIds: number[],
    matriculaMotorista?: number,
    numeroVeiculo?: number
  ): Observable<any> {
    // Buscar identificador do AuthService
    return from(this.authService.getIdentificador()).pipe(
      switchMap((identificador: string) => {
        console.log('🔍 Identificador do AuthService:', identificador);

        const payload: any = {
          folhasIds: folhasIds,
          matriculaMotorista: matriculaMotorista || null,
          numeroVeiculo: numeroVeiculo || null,
          usuario: identificador || 'SISTEMA'
        };

        console.log('📤 Payload final:', payload);
        return this.http.post(`/folha-servico/atribuir-motorista-veiculo`, payload);
      })
    );
  }

  buscarTarefasPorPapeleta(papeleta: number): Observable<any> {
    return this.http.get(`/folha-servico/tarefas-por-papeleta/${papeleta}`);
  }

  substituirMotorista(
    papeleta: number,
    novaMatriculaMotorista: number,
    novoNumeroVeiculo?: number,
    motivoInatividade?: string,
    identificadorUsuario?: string
  ): Observable<any> {
    const body = {
      papeleta: papeleta,
      novaMatriculaMotorista: novaMatriculaMotorista,
      novoNumeroVeiculo: novoNumeroVeiculo || null,
      motivoInatividade: motivoInatividade || '',
      identificadorUsuario: identificadorUsuario || ''
    };

    return this.http.post(
      `/folha-servico/substituir-motorista`,
      body
    );
  }

  listarFolhasCompletasPorDataEMatricula(dataServico: string, matricula: number): Observable<FolhaTransferenciaDTO[]> {
    const params = new HttpParams()
      .set('dataServico', dataServico)
      .set('matricula', matricula.toString());

    return this.http.get<any>(
      `/folha-servico/listar-por-data-matricula`,
      { params }
    ).pipe(
      map(response => {
        if (response && response.folhas) {
          return response.folhas;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      })
    );
  }

  removerMotoristaDaFolha(folhaId: number): Observable<any> {
    return this.http.post(
      `/folha-servico/${folhaId}/remover-motorista`,
      {}
    );
  }

  removerVeiculoDaFolha(request: RemoverVeiculoRequestDTO): Observable<TransferenciaResultadoDTO> {
    return this.http.post<TransferenciaResultadoDTO>(
      `/folha-servico/remover-veiculo`,
      request
    );
  }

  salvarObservacaoPlanejamento(folhaId: number, observacao: string | null): Observable<any> {
    const body = { observacao: observacao };
    return this.http.post(`/folha-servico/${folhaId}/observacao-planejamento`, body);
  }

}
